import {
  Box,
  ModalProps,
  Stack,
  StackProps,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import { ReactNode } from 'react';

import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_LG } from '@/styles/tokens/panelWidths';

interface SideDrawerBaseProps {
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Function to call when closing the drawer
   */
  onClose: () => void;
  /**
   * The width of the drawer in pixels or CSS value
   * @default OVERLAY_LG (800)
   */
  width?: number | string;
  /**
   * The anchor position of the drawer
   * @default "right"
   */
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  /**
   * Data test ID for testing
   */
  'data-testid'?: string;
  /**
   * If true, the backdrop (scrim) is not rendered
   * @default false
   */
  hideBackdrop?: boolean;
  /**
   * The styles to apply to the drawer paper
   * @default {}
   */
  drawerPropsSx?: SxProps<Theme>;
  /**
   * Additional props to pass to the underlying MUI Modal
   */
  modalProps?: Partial<Omit<ModalProps, 'open'>>;
}

/**
 * Structured mode: renders a built-in header, scrollable body, and optional footer.
 */
export interface SideDrawerProps extends SideDrawerBaseProps {
  heading: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
  headerProps?: StackProps;
  bodyPropsSx?: SxProps<Theme>;
}

/**
 * Children mode: renders raw children without the built-in header/body/footer layout.
 * Use this when the drawer content is a self-contained component with its own chrome.
 */
export interface SideDrawerChildrenProps extends SideDrawerBaseProps {
  children: ReactNode;
}

/**
 * SideDrawer component that slides in from the side of the screen.
 *
 * Two usage modes:
 * - **Structured**: pass `heading`, `body`, and optional `footer` for the built-in layout.
 * - **Children**: pass `children` for a raw wrapper around self-contained content.
 */
export function SideDrawer(props: SideDrawerProps | SideDrawerChildrenProps) {
  const {
    isOpen,
    onClose,
    width = OVERLAY_LG,
    anchor = 'right',
    'data-testid': dataTestId,
    drawerPropsSx,
    hideBackdrop = false,
    modalProps,
  } = props;

  const theme = useTheme();

  const hideBackdropSx = hideBackdrop
    ? {
        pointerEvents: 'none',
        '& .MuiDialog-container': {
          pointerEvents: 'none',
        },
        '& .MuiPaper-root': {
          pointerEvents: 'auto',
        },
      }
    : undefined;

  const mergedModalProps: Partial<ModalProps> = {
    ...modalProps,
    // Deep-merge sx so that hideBackdrop pointer-events are preserved
    // even when the caller provides their own modalProps.sx
    ...(hideBackdropSx && {
      sx: { ...hideBackdropSx, ...modalProps?.sx } as ModalProps['sx'],
    }),
  };

  return (
    <Drawer
      anchor={anchor}
      open={isOpen}
      onClose={onClose}
      data-testid={dataTestId}
      hideBackdrop={hideBackdrop}
      ModalProps={
        Object.keys(mergedModalProps).length > 0 ? mergedModalProps : undefined
      }
      PaperProps={{
        sx: {
          width,
          display: 'flex',
          flexDirection: 'column',
          ...drawerPropsSx,
        },
      }}
    >
      {'children' in props ? (
        props.children
      ) : (
        <SideDrawerStructuredContent {...props} theme={theme} />
      )}
    </Drawer>
  );
}

function SideDrawerStructuredContent({
  onClose,
  heading,
  body,
  footer,
  headerProps,
  bodyPropsSx,
  theme,
}: SideDrawerProps & { theme: Theme }) {
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        top={0}
        component="header"
        sx={{
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
          pr: 2,
          bgcolor: COLORS.PRIMITIVES.WHITE,
          zIndex: theme.zIndex.drawer + 1,
          height: 'auto',
        }}
        {...headerProps}
      >
        <DialogTitle
          sx={{
            flex: 'unset',
          }}
          variant="title-l"
        >
          {heading}
        </DialogTitle>
        <CloseButton
          data-testid="CloseDrawerButton"
          variant="transparent"
          size="sm"
          onClick={onClose}
        />
      </Stack>
      <Box
        px={2}
        pb={3}
        sx={{
          flexGrow: 1,
          overflowY: 'scroll',
          height: '100%',
          display: 'flex',
          ...bodyPropsSx,
        }}
      >
        {body}
      </Box>
      {footer && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${COLORS.GRAY[200]}`,
            height: 'auto',
          }}
        >
          {footer}
        </Box>
      )}
    </>
  );
}
